import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ColourConversionAlgorithm, ColourConverter } from "../util/colour_converter";
import { Stencil } from "./types";

interface StencilCreateDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (stencil: Stencil) => void;
    palette: string[];
}

export const StencilCreateDialog: React.FC<StencilCreateDialogProps> = ({
    open,
    onClose,
    onSave,
    palette
}) => {
    const [name, setName] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [algorithm, setAlgorithm] = useState<ColourConversionAlgorithm>(ColourConversionAlgorithm.PERCEPTUAL);
    const [preview, setPreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pixels, setPixels] = useState<Array<{x: number, y: number, colour: string}>>([]);
    const [dimensions, setDimensions] = useState<{width: number, height: number} | null>(null);

    const colorConverter = new ColourConverter(palette);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];

            // Check if file is HEIC/HEIF
            if (selectedFile.type === "image/heic" || selectedFile.type === "image/heif") {
                setError("HEIC/HEIF images are not supported. Please convert to JPEG or PNG first.");
                return;
            }

            if (!selectedFile.type.startsWith("image/")) {
                setError("Please select an image file (JPEG, PNG, GIF, etc)");
                return;
            }

            try {
                setFile(selectedFile);
                await processImage(selectedFile);
            } catch (err) {
                setError("Failed to process image. Please try a different image.");
                console.error("Image processing error:", err);
            }
        }
    };

    const processImage = async (imageFile: File) => {
        const img = new Image();
        img.src = URL.createObjectURL(imageFile);
        await img.decode();

        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        const maxDim = 77;
        const ratio = width / height;

        if (width > height) {
            width = Math.min(width, maxDim);
            height = Math.floor(width / ratio);
        } else {
            height = Math.min(height, maxDim);
            width = Math.floor(height * ratio);
        }

        // Create canvas for downsampled image
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;

        // Draw image at new size
        ctx.drawImage(img, 0, 0, width, height);

        // Convert colors
        const imageData = ctx.getImageData(0, 0, width, height);
        const result = colorConverter.convertImage(imageData, algorithm);
        setPixels(result.pixels);
        setDimensions({ width: result.width, height: result.height });

        // Create preview
        const previewData = colorConverter.convertToRgbaArray(result.pixels, width, height);
        const previewImageData = new ImageData(previewData, width, height);
        ctx.putImageData(previewImageData, 0, 0);
        setPreview(canvas.toDataURL());
    };

    const updatePreview = useCallback(async () => {
        if (!file) return;
        await processImage(file);
    }, [file, algorithm, processImage]);

    useEffect(() => {
        updatePreview();
    }, [algorithm, updatePreview]);

    const handleClose = () => {
        // Clean up
        if (preview) {
            URL.revokeObjectURL(preview);
        }
        setFile(null);
        setPreview(null);
        setError(null);
        setName("");

        // Notify parent
        onClose();

        // Dispatch close event
        document.dispatchEvent(new CustomEvent("stencil-dialog-closed"));
    };

    const handleSave = () => {
        if (!file || !dimensions || !name.trim()) {
            setError("Please fill in all fields");
            return;
        }

        const stencil: Stencil = {
            id: uuidv4(),
            name: name.trim(),
            pixels,
            width: dimensions.width,
            height: dimensions.height,
            algorithm
        };

        onSave(stencil);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Stencil</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Stencil Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="stencil-file"
                    type="file"
                    onChange={handleFileChange}
                />
                <label htmlFor="stencil-file">
                    <Button component="span" variant="contained" style={{ marginTop: 16 }}>
                        Upload Image
                    </Button>
                </label>
                <FormControl fullWidth style={{ marginTop: 16 }}>
                    <InputLabel>Color Conversion Algorithm</InputLabel>
                    <Select
                        value={algorithm}
                        onChange={(e) => setAlgorithm(e.target.value as ColourConversionAlgorithm)}
                    >
                        <MenuItem value={ColourConversionAlgorithm.PERCEPTUAL}>Perceptual RGB</MenuItem>
                        <MenuItem value={ColourConversionAlgorithm.LAB}>CIE LAB</MenuItem>
                        <MenuItem value={ColourConversionAlgorithm.EUCLIDEAN}>Simple RGB</MenuItem>
                        <MenuItem value={ColourConversionAlgorithm.DITHER}>Dithering</MenuItem>
                    </Select>
                </FormControl>
                {preview && (
                    <div style={{ marginTop: 16, textAlign: "center" }}>
                        <h4>Preview</h4>
                        <img
                            src={preview}
                            alt="Stencil preview"
                            style={{
                                border: "1px solid #ccc",
                                imageRendering: "pixelated",
                                width: "200px"
                            }}
                        />
                    </div>
                )}
                {error && (
                    <div style={{ color: "red", marginTop: 16 }}>
                        {error}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Save Stencil
                </Button>
            </DialogActions>
        </Dialog>
    );
};
